import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  JOURNEY_PLANNER_DEFAULT_ZOOM,
  getJourneyLayoutAdjustedJourneys,
  getNextJourneyPlannerZoomLevel,
} from "./journeyPlannerHelpers";
import {
  addAutomationToJourneyPlanner,
  addJourneyToPlanner,
  defaultJourneyName,
  getDefaultPlanner,
  getFilteredPlannerAutomations,
  getNextJourneyCreatePosition,
  plannerTabs,
  removeJourneyFromPlanner,
  removeJourneyItemFromPlanner,
  reorderJourneyItemsInPlanner,
  replaceJourneyInPlanner,
} from "./journeyPlannerState";
import {
  ActiveDrag,
  Journey,
  JourneyPlannerContextValue,
  JourneyRefs,
  Planner,
  PlannerAutomation,
  PlannerNavigation,
} from "./journeyPlanner.types";

const JourneyPlannerContext = createContext<JourneyPlannerContextValue | null>(null);

const MAX_HISTORY = 100;

function JourneyPlannerProvider({ children }: { children: ReactNode }) {
  const [planner, setPlanner] = useState<Planner>(getDefaultPlanner);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [activeTab, setActiveTab] = useState("Daily");
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  const [zoomLevel, setZoomLevel] = useState(
    JOURNEY_PLANNER_DEFAULT_ZOOM
  );
  const [journeyPendingDelete, setJourneyPendingDelete] =
    useState<Journey | null>(null);
  const plannerRef = useRef<Planner>(getDefaultPlanner());
  const journeyRefs: JourneyRefs = useRef({});
  const shouldAdjustJourneyLayoutRef = useRef(false);
  const nextJourneyIdRef = useRef(-1);
  const nextJourneyItemIdRef = useRef(-1);
  const [plannerNavigation, setPlannerNavigation] = useState<PlannerNavigation>({
    isDirty: false,
    onSave: null,
    saving: false,
  });
  plannerRef.current = planner;

  const setPlannerState = useCallback((updater: Planner | ((current: Planner) => Planner)) => {
    setPlanner((current) => {
      const nextPlanner =
        typeof updater === "function" ? updater(current) : updater;
      plannerRef.current = nextPlanner;
      return nextPlanner;
    });
  }, []);

  const historyRef = useRef<{ past: Planner[]; future: Planner[] }>({
    past: [],
    future: [],
  });
  const [history, setHistory] = useState({
    canUndo: false,
    canRedo: false,
  });

  const syncHistory = useCallback(() => {
    const { past, future } = historyRef.current;
    setHistory({ canUndo: past.length > 0, canRedo: future.length > 0 });
  }, []);

  // A planner that arrives from the server has new ids, so earlier states no longer fit.
  const loadPlannerState = useCallback(
    (updater: Planner | ((current: Planner) => Planner)) => {
      historyRef.current = { past: [], future: [] };
      setPlannerState(updater);
      syncHistory();
    },
    [setPlannerState, syncHistory]
  );

  const changePlanner = useCallback(
    (updater: (current: Planner) => Planner) => {
      const before = plannerRef.current;
      const { past } = historyRef.current;

      past.push(before);
      if (past.length > MAX_HISTORY) {
        past.shift();
      }
      historyRef.current.future = [];
      setPlannerState(updater(before));
      setIsDirty(true);
      syncHistory();
    },
    [setPlannerState, syncHistory]
  );

  const undo = useCallback(() => {
    const { past, future } = historyRef.current;

    if (past.length === 0) {
      return;
    }

    future.push(plannerRef.current);
    setPlannerState(past.pop() as Planner);
    setIsDirty(true);
    syncHistory();
  }, [setPlannerState, syncHistory]);

  const redo = useCallback(() => {
    const { past, future } = historyRef.current;

    if (future.length === 0) {
      return;
    }

    past.push(plannerRef.current);
    setPlannerState(future.pop() as Planner);
    setIsDirty(true);
    syncHistory();
  }, [setPlannerState, syncHistory]);

  const replaceJourney = useCallback((updatedJourney: Journey) => {
    changePlanner((current) =>
      replaceJourneyInPlanner(current, updatedJourney)
    );
  }, [changePlanner]);

  const handleCreateJourney = useCallback(() => {
    const nextPosition = getNextJourneyCreatePosition(
      planner.Journeys
    );

    changePlanner((current) =>
      addJourneyToPlanner(current, {
        Id: nextJourneyIdRef.current--,
        Name: defaultJourneyName,
        X: nextPosition.x,
        Y: nextPosition.y,
        ModifiedOn: null,
        Items: [],
      })
    );
  }, [planner.Journeys, changePlanner]);

  const handleDeleteJourney = useCallback((journeyId: number) => {
    changePlanner((current) =>
      removeJourneyFromPlanner(current, journeyId)
    );
    setJourneyPendingDelete(null);
  }, [changePlanner]);

  const handleRemoveJourneyItem = useCallback((journeyId: number, itemId: number) => {
    changePlanner((current) =>
      removeJourneyItemFromPlanner(current, journeyId, itemId)
    );
  }, [changePlanner]);

  const addAutomationToJourney = useCallback(
    (
      journeyId: number,
      draggedAutomation: PlannerAutomation,
      insertIndex: number | null = null
    ) => {
      shouldAdjustJourneyLayoutRef.current = true;
      changePlanner((current) =>
        addAutomationToJourneyPlanner(
          current,
          journeyId,
          draggedAutomation,
          nextJourneyItemIdRef.current--,
          insertIndex
        )
      );
    },
    [changePlanner]
  );

  useLayoutEffect(() => {
    if (!shouldAdjustJourneyLayoutRef.current) {
      return;
    }

    shouldAdjustJourneyLayoutRef.current = false;

    const adjustedJourneys = getJourneyLayoutAdjustedJourneys(
      planner.Journeys,
      journeyRefs
    );

    if (adjustedJourneys === planner.Journeys) {
      return;
    }

    setPlannerState({
      ...planner,
      Journeys: adjustedJourneys,
    });
  }, [planner, setPlannerState]);

  const reorderJourneyItems = useCallback(
    (journeyId: number, oldIndex: number, newIndex: number) => {
      changePlanner((current) =>
        reorderJourneyItemsInPlanner(
          current,
          journeyId,
          oldIndex,
          newIndex
        )
      );
    },
    [changePlanner]
  );

  const handleZoomIn = useCallback(() => {
    setZoomLevel((current) =>
      getNextJourneyPlannerZoomLevel(current, "in")
    );
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((current) =>
      getNextJourneyPlannerZoomLevel(current, "out")
    );
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(JOURNEY_PLANNER_DEFAULT_ZOOM);
  }, []);

  const value = useMemo<JourneyPlannerContextValue>(
    () => ({
      activeDrag,
      activeTab,
      addAutomationToJourney,
      defaultJourneyName,
      filteredAutomations: getFilteredPlannerAutomations(
        planner,
        activeTab
      ),
      handleCreateJourney,
      handleDeleteJourney,
      handleRemoveJourneyItem,
      handleZoomIn,
      handleZoomOut,
      handleZoomReset,
      canRedo: history.canRedo,
      canUndo: history.canUndo,
      isDirty,
      journeyPendingDelete,
      plannerRef,
      journeyRefs,
      loading,
      planner,
      plannerNavigation,
      plannerTabs,
      replaceJourney,
      reorderJourneyItems,
      saving,
      setActiveDrag,
      setActiveTab,
      setIsDirty,
      setJourneyPendingDelete,
      setLoading,
      setPlanner,
      redo,
      setPlannerState: loadPlannerState,
      setPlannerNavigation,
      setSaving,
      undo,
      zoomLevel,
    }),
    [
      activeDrag,
      activeTab,
      addAutomationToJourney,
      handleCreateJourney,
      handleDeleteJourney,
      handleRemoveJourneyItem,
      handleZoomIn,
      handleZoomOut,
      handleZoomReset,
      history,
      isDirty,
      journeyPendingDelete,
      loadPlannerState,
      loading,
      planner,
      plannerRef,
      plannerNavigation,
      replaceJourney,
      redo,
      reorderJourneyItems,
      saving,
      undo,
      zoomLevel,
    ]
  );

  return (
    <JourneyPlannerContext.Provider value={value}>
      {children}
    </JourneyPlannerContext.Provider>
  );
}

function useJourneyPlanner(): JourneyPlannerContextValue {
  const context = useContext(JourneyPlannerContext);

  if (!context) {
    throw new Error(
      "useJourneyPlanner must be used within JourneyPlannerProvider"
    );
  }

  return context;
}

export { JourneyPlannerProvider, useJourneyPlanner };
